<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrpc } from '~/composables/useTrpc';
import type { Hero, HeroSlider, Slide } from '~/types/hero';
import { useLocalization } from '~/composables/useLocalization';
import { useSkeletonGate } from '~/composables/useSkeletonGate';
import HeroSliderComponent from '~/components/sliders/HeroSlider.vue';
import type { Swiper as SwiperType } from 'swiper/types';
import { useAsyncData, useHead } from '#imports';

interface HeroConfig {
  layout?: 'split-columns' | 'full-width';
  autoplay?: boolean;
  interval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  videoWidth?: string;
  sliderWidth?: string;
  videoPosition?: 'left' | 'right';
  sliderPosition?: 'left' | 'right';
  overlay?: {
    enabled: boolean;
    color?: string;
    opacity?: number;
    gradient?: {
      from: string;
      to: string;
      direction: string;
    };
  };
  overlayOpacity?: string;
}

interface Props {
  slides?: Slide[];
  config?: HeroConfig;
  titleTag?: string;
  fallbackTitleTag?: string;
}

const props = withDefaults(defineProps<Props>(), {
  slides: () => [],
  config: () => ({
    layout: 'full-width',
    autoplay: true,
    interval: 5000,
    showDots: true,
    showArrows: true,
    overlay: {
      enabled: true,
      color: 'black',
      opacity: 0.5,
      gradient: {
        from: 'rgba(0,0,0,0.7)',
        to: 'rgba(0,0,0,0)',
        direction: 'to-t'
      }
    }
  }),
  titleTag: 'h2',
  fallbackTitleTag: 'div',
});

const { t } = useI18n();
const { t: localT } = useLocalization();
const currentSlide = ref(0);
const error = ref<Error | null>(null);
const hasMounted = ref(false);

onMounted(() => {
  hasMounted.value = true;
});

const trpc = useTrpc();
const { shouldShowSkeleton } = useSkeletonGate();
const heroThemeId = computed(() => props.config?.themeId ?? null);
const heroDataKey = computed(() => `hero-full-width-data-${heroThemeId.value ?? 'default'}`);
const { data: heroPayload, pending: isLoading } = await useAsyncData(
  heroDataKey,
  async () => {
    const heroResultPromise = trpc.hero.getHero.query();
    const sliderResultPromise = trpc.hero.getHeroSliders.query({ themeId: heroThemeId.value ?? undefined });
    const [heroResult, initialSliderResult] = await Promise.all([
      heroResultPromise,
      sliderResultPromise,
    ]);

    return {
      heroData: (heroResult || []) as Hero[],
      sliderData: (initialSliderResult || []) as HeroSlider[],
    };
  },
  {
    watch: [heroThemeId],
    default: () => ({
      heroData: [] as Hero[],
      sliderData: [] as HeroSlider[],
    }),
  },
);

const heroData = computed(() => heroPayload.value?.heroData || []);
const sliderData = computed(() => heroPayload.value?.sliderData || []);

const hero = computed(() => {
  if (heroData.value.length > 0) {
    return heroData.value[0];
  }
  return null;
});

const sliders = computed(() => {
  return sliderData.value || [];
});

const swiperOptions = computed(() => ({
  modules: [Navigation, Pagination, Autoplay, EffectFade],
  slidesPerView: 1,
  slidesPerGroup: 1,
  effect: 'fade',
  loop: true,
  loopPreventsSliding: false,
  loopAddBlankSlides: true,
  speed: 800,
  allowTouchMove: true,
  watchOverflow: true,
  navigation: {
    nextEl: '.hero-swiper-next',
    prevEl: '.hero-swiper-prev',
    enabled: true
  },
  pagination: props.config.showDots ? {
    el: '.hero-swiper-pagination',
    clickable: true,
    enabled: true
  } : false,
  autoplay: props.config.autoplay ? {
    delay: props.config.interval || 5000,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
    stopOnLastSlide: false,
    waitForTransition: false
  } : false
}));

const sortedSlides = computed(() => {
  if (sliderData.value.length > 0) {
    return [...sliderData.value]
      .sort((a, b) => a.order - b.order)
      .map(slider => ({
        image_url: slider.imageUrl,
        title: slider.title,
        description: slider.description || '',
        link: slider.buttonLink || '#',
        buttonText: slider.buttonText,
        order: slider.order
      }));
  }
  return [...props.slides].sort((a, b) => a.order - b.order);
});

const heroPreloadImage = computed(() => sortedSlides.value[0]?.image_url || null);

useHead(() => ({
  link: heroPreloadImage.value
    ? [
        {
          key: 'hero-lcp-image',
          rel: 'preload',
          as: 'image',
          href: heroPreloadImage.value,
          fetchpriority: 'high',
        },
      ]
    : [],
}));


// Thêm computed property để xử lý config
const processedConfig = computed(() => {
  if (!props.config) return props.config;
  
  const config = {
    ...props.config,
    overlay: {
      enabled: props.config.overlay?.enabled ?? true,
      opacity: props.config.overlayOpacity 
        ? parseFloat(props.config.overlayOpacity)
        : props.config.overlay?.opacity ?? 0.5,
      gradient: props.config.overlay?.gradient ?? {
        from: 'rgba(0,0,0,0.7)',
        to: 'rgba(0,0,0,0)',
        direction: 'to-t'
      }
    }
  };

  return config;
});

</script>

<template>
  <section class="hero-section-full relative w-full md:pt-0">
    <div class="aspect-[4/3] md:aspect-[1780/450] w-full">
      <div v-if="shouldShowSkeleton || isLoading" class="w-full h-full">
        <HeroSkeleton class="h-full" overlay-card />
      </div>
      
      <div v-else-if="error" class="flex items-center justify-center w-full h-full">
        <p class="text-red-500">{{ error.message }}</p>
      </div>
      
      <div
        v-else-if="sortedSlides.length > 0 && !hasMounted"
        class="w-full h-full relative"
      >
        <div class="relative h-full w-full">
          <div class="relative h-full w-full">
            <AppImage
              class="absolute inset-0 w-full h-full z-[1]"
              :src="sortedSlides[0].image_url"
              :alt="sortedSlides[0].title"
              width="1780"
              height="450"
              sizes="(max-width: 768px) 100vw, 1780px"
              format="webp"
              :quality="75"
              priority
              loading="eager"
              fetchpriority="high"
              customClass="w-full h-full object-cover object-center"
            />

            <div
              v-if="processedConfig.overlay?.enabled"
              class="absolute inset-0 z-[2]"
              :class="{
                'bg-gradient-to-t from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-t',
                'bg-gradient-to-b from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-b',
                'bg-gradient-to-l from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-l',
                'bg-gradient-to-r from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-r'
              }"
              :style="{
                '--tw-gradient-from': processedConfig.overlay.gradient?.from || 'rgb(0 0 0 / 0.7)',
                '--tw-gradient-to': processedConfig.overlay.gradient?.to || 'rgb(0 0 0 / 0)',
                '--tw-gradient-stops': processedConfig.overlay.gradient
                  ? `${processedConfig.overlay.gradient.from}, ${processedConfig.overlay.gradient.to}`
                  : undefined
              }"
            ></div>

            <div
              v-if="processedConfig?.overlay?.enabled"
              class="absolute inset-0 flex items-center justify-center z-[3]"
            >
              <div class="container mx-auto px-4 text-center text-white">
                <component
                  :is="titleTag"
                  class="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-2 md:mb-4 drop-shadow-lg"
                >
                  {{ sortedSlides[0].title }}
                </component>
                <p class="text-base sm:text-lg md:text-xl mb-4 md:mb-8 max-w-2xl mx-auto drop-shadow line-clamp-3 md:line-clamp-none">
                  {{ sortedSlides[0].description }}
                </p>
                <NuxtLink
                  v-if="sortedSlides[0].link"
                  :to="sortedSlides[0].link"
                  class="inline-block bg-primary hover:bg-primary/90 text-white font-semibold py-2 md:py-3 px-6 md:px-8 rounded-lg transition-colors text-sm md:text-base"
                >
                  {{ sortedSlides[0].buttonText || localT('common.learn_more') }}
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>

        <div v-if="processedConfig.showArrows" class="hero-swiper-prev swiper-button-prev !z-10 !hidden md:!flex"></div>
        <div v-if="processedConfig.showArrows" class="hero-swiper-next swiper-button-next !z-10 !hidden md:!flex"></div>
      </div>

      <div v-else class="w-full h-full relative">
        <Swiper v-if="sortedSlides.length > 0"
                v-bind="swiperOptions"
                class="w-full h-full hero-swiper">
          <SwiperSlide
            v-for="(slide, index) in sortedSlides"
            :key="slide.order"
            class="relative"
          >
            <div class="relative w-full h-full">
              <!-- Image layer (z-index: 1) -->
              <AppImage
                class="absolute inset-0 w-full h-full z-[1]"
                :src="slide.image_url"
                :alt="slide.title"
                width="1780"
                height="450"
                sizes="(max-width: 768px) 100vw, 1780px"
                format="webp"
                :quality="75"
                :priority="index === 0"
                :loading="index === 0 ? 'eager' : 'lazy'"
                :fetchpriority="index === 0 ? 'high' : 'auto'"
                customClass="w-full h-full object-cover object-center"
              />
              
              <!-- Dark overlay layer (z-index: 2) -->
              <div v-if="processedConfig.overlay?.enabled" 
                   class="absolute inset-0 z-[2]"
                   :class="{
                     'bg-gradient-to-t from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-t',
                     'bg-gradient-to-b from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-b',
                     'bg-gradient-to-l from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-l',
                     'bg-gradient-to-r from-black/70 to-transparent': processedConfig.overlay.gradient?.direction === 'to-r'
                   }"
                   :style="{
                     '--tw-gradient-from': processedConfig.overlay.gradient?.from || 'rgb(0 0 0 / 0.7)',
                     '--tw-gradient-to': processedConfig.overlay.gradient?.to || 'rgb(0 0 0 / 0)',
                     '--tw-gradient-stops': processedConfig.overlay.gradient 
                       ? `${processedConfig.overlay.gradient.from}, ${processedConfig.overlay.gradient.to}`
                       : undefined
                   }">
              </div>
              
              <!-- Content layer (z-index: 3) -->
              <div class="absolute inset-0 flex items-center justify-center z-[3]" v-if="processedConfig?.overlay?.enabled">
                <div class="container mx-auto px-4 text-center text-white">
                  <component
                    :is="index === 0 ? titleTag : fallbackTitleTag"
                    class="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-2 md:mb-4 drop-shadow-lg"
                  >
                    {{ slide.title }}
                  </component>
                  <p class="text-base sm:text-lg md:text-xl mb-4 md:mb-8 max-w-2xl mx-auto drop-shadow line-clamp-3 md:line-clamp-none">
                    {{ slide.description }}
                  </p>
                  <NuxtLink 
                    v-if="slide.link"
                    :to="slide.link"
                    class="inline-block bg-primary hover:bg-primary/90 text-white font-semibold py-2 md:py-3 px-6 md:px-8 rounded-lg transition-colors text-sm md:text-base"
                  >
                    {{ slide.buttonText || localT('common.learn_more') }}
                  </NuxtLink>
                </div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
        <div v-else class="flex items-center justify-center w-full h-full bg-gray-100 dark:bg-gray-800">
          <p class="text-gray-500 dark:text-gray-400">{{ localT('common.no_slides') }}</p>
        </div>

        <!-- Điều chỉnh vị trí và kích thước navigation cho mobile -->
        <div v-if="processedConfig.showArrows" class="hero-swiper-prev swiper-button-prev !z-10 !hidden md:!flex"></div>
        <div v-if="processedConfig.showArrows" class="hero-swiper-next swiper-button-next !z-10 !hidden md:!flex"></div>
        <div v-if="processedConfig.showDots" class="hero-swiper-pagination !absolute !bottom-4 md:!bottom-8 !left-1/2 !-translate-x-1/2 !z-10 !w-auto"></div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.hero-section-full {
  position: relative;
  
  :deep(.hero-swiper) {
    height: 100%;
  }
  
  // Điều chỉnh kích thước navigation buttons cho mobile
  :deep(.swiper-button-next),
  :deep(.swiper-button-prev) {
    @apply w-8 h-8 md:w-12 md:h-12;
    
    &::after {
      @apply text-base md:text-xl;
    }
  }
  
  // Điều chỉnh kích thước dots cho mobile
  :deep(.swiper-pagination-bullet) {
    @apply w-2 h-2 md:w-3 md:h-3;
  }
}
</style> 
